import type { ParentChild } from "../types/parentFamily";

export function getChildFullName(child: Pick<ParentChild, "firstName" | "lastName">): string {
  return [child.firstName, child.lastName].filter(Boolean).join(" ").trim();
}

export function getChildInitials(child: Pick<ParentChild, "firstName" | "lastName">): string {
  const first = child.firstName?.trim()?.[0] || "";
  const last = child.lastName?.trim()?.[0] || "";
  return (first + last).toUpperCase() || "?";
}

/**
 * e.g. grade "4" + class "R" → "Grade 4R"; grade "R" → "Grade R". A class that
 * already starts with the grade, like "Grade 3" + "3A", isn't doubled up.
 */
export function formatChildGradeLabel(
  child: Pick<ParentChild, "grade" | "classSection">
): string | null {
  const grade = (child.grade?.trim() || "").replace(/^grade\s*/i, "");
  const section = child.classSection?.trim() || "";
  if (!grade && !section) return null;

  const lowerGrade = grade.toLowerCase();
  const lowerSection = section.toLowerCase();
  let core = grade;
  if (section && !lowerGrade.endsWith(lowerSection)) {
    core = lowerSection.startsWith(lowerGrade) ? section : `${grade}${section}`;
  }
  return `Grade ${core}`;
}

export function calculateAgeFromDob(dateOfBirth?: string): number | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

export function formatCurrency(amount: number, currency = "ZAR"): string {
  try {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `R${amount.toFixed(2)}`;
  }
}

export function formatShortDate(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function generateStudentId(): string {
  const num = Math.floor(10000 + Math.random() * 90000);
  return `NB-${num}`;
}
